import { TimeAbbreviation } from 'components/designSystem/guidelines/TimeAbbreviation';
import { Page } from 'components/shared/Page';
import { Section } from 'components/shared/Section';

const absoluteTime = [
    <div key="1">
        <p>
            Absolute time is used when a user needs to look back on past content and retrieve
            information. e.g. Referral Received Date, Created Date, etc.
        </p>
        <p>
            Use AM and PM, preceded by a space. Use capital letters for AM and PM. Use the following
            format for dates: month day, year, as in July 31, 2016. Don&#39;t use day month year, as
            in 31 July 2016.
        </p>
    </div>,
    <div key="2">
        <h3>Date Range&ensp;|&ensp;MM/DD/YYYY &ndash; MM/DD/YYYY</h3>
        <p>Use when formatting a start and end date.</p>
        <h4>Examples</h4>
        <ul>
            <li>Auth Periods</li>
            <li>Templates</li>
        </ul>
    </div>,
    <div key="3">
        <h3>Date Stamp&ensp;|&ensp;MM/DD/YYYY</h3>
        <p>Use when formatting a set date and when a time stamp would not be considered vital.</p>
        <h4>Examples</h4>
        <ul>
            <li>Date of Birth</li>
            <li>Referral Expiration</li>
            <li>Worklist Task Due Date</li>
            <li>Worklist Task Evaluated On Date</li>
            <li>Last Updated Column</li>
        </ul>
    </div>,
    <div key="4">
        <h3>Date & Time Stamp&ensp;|&ensp;MM/DD/YYYY HH:MM AM/PM</h3>
        <p>
            Use when formatting a set date and when a time stamp would be considered vital. For
            instances where the hour is a single digit number do not add a leading zero to the time
            &quot;9:00AM.&quot;
        </p>
        <h4>Examples</h4>
        <ul>
            <li>Start Date</li>
            <li>Servicing Start Date</li>
            <li>Submitted Date/Time</li>
            <li>Updated On</li>
            <li>Received Date</li>
        </ul>
    </div>,
    <div key="5">
        <h3>Written Date Stamp&ensp;|&ensp;September 3, 2024</h3>
        <p>
            Use when formatting the date of an event. This format is used when there is space to
            write out the month but the day of the week is not vital to know.
        </p>
        <h4>Examples</h4>
        <ul>
            <li>Announcements</li>
            <li>Updates</li>
        </ul>
    </div>,
    <div key="6">
        <h3>Written Day & Date&ensp;|&ensp;Wednesday, January 11, 2024</h3>
        <p>
            Use when formatting the date of an event. This format is used when there is space to
            write out the month and the day of the week is vital to know.
        </p>
        <h4>Examples</h4>
        <ul>
            <li>Urgent Messages</li>
            <li>Timeline</li>
        </ul>
    </div>,
    <div key="7">
        <h3>Sentence&ensp;|&ensp;Created on Tuesday, September 3, 2024</h3>
        <p>
            Use when the date needs to be included in a sentence. If it is considered to be vital
            the time stamp can be included after the date &quot;Created on Tuesday, September 3,
            2024 at 11:00 AM.&quot;
        </p>
        <h4>Examples</h4>
        <ul>
            <li>Inline Messages</li>
            <li>Completed Worklist Tasks</li>
            <li>Pending Referrals </li>
        </ul>
    </div>,
];

const relativeTime = [
    <div key="8">
        <p>
            Relative timestamps display the number of minutes, hours, days, weeks, or years. Use
            relative time when a user needs to know how long ago something happened. e.g. worklist
            task, a notification, etc.
        </p>
        <p>Shows elapsed time. Use when space is limited.</p>
        <h4>Examples</h4>
        <ul>
            <li>Worklist Time Badge</li>
            <li>Notifications</li>
        </ul>
    </div>,
    <div key="9">
        <div className="sds_guidelines__header">
            <div style={{ width: '20%' }} className="sds_guidelines__column-header">
                Unit
            </div>
            <div style={{ width: '20%' }} className="sds_guidelines__column-header">
                Abbr
            </div>
            <div style={{ width: '20%' }} className="sds_guidelines__column-header">
                Short Display
            </div>
            <div style={{ width: '20%' }} className="sds_guidelines__column-header">
                Long Display
            </div>
            <div style={{ width: '20%' }} className="sds_guidelines__column-header">
                Guide
            </div>
        </div>
        <div className="sds_example__section">
            <TimeAbbreviation
                unit="day, days"
                abbreviation="d"
                short="3d ago"
                long="3 days ago"
                guide="Abbreviate unless used in a sentence."
            />
        </div>
        <div className="sds_example__section">
            <TimeAbbreviation
                unit="hour, hours"
                abbreviation="h"
                short="21h ago"
                long="21 hours ago"
                guide="Abbreviate unless used in a sentence."
            />
        </div>
        <div className="sds_example__section">
            <TimeAbbreviation
                unit="minute, minutes"
                abbreviation="m"
                short="12m ago"
                long="12 minutes ago"
            />
        </div>
        <div className="sds_example__section">
            <TimeAbbreviation
                unit="second, seconds"
                abbreviation="s"
                short="33s ago"
                long="33 seconds ago"
            />
        </div>
        <div className="sds_example__section">
            <TimeAbbreviation
                unit="week, weeks"
                abbreviation="wk"
                short="3wk ago"
                long="3 weeks ago"
                guide="Abbreviate unless used in a sentence."
            />
        </div>
        <div className="sds_example__section">
            <TimeAbbreviation
                unit="month, months"
                abbreviation="mon"
                short="2mon ago"
                long="2 months ago"
            />
        </div>
        <div className="sds_example__section">
            <TimeAbbreviation
                unit="year, years"
                abbreviation="yr"
                short="2yr ago"
                long="2 years ago"
                guide="Abbreviate unless used in a sentence."
            />
        </div>
    </div>,
];

export const DateTime = () => (
    <div>
        <Page
            pageTitle="Date & Time"
            pageDescription={
                <p>
                    Date and time formatting varies based on its context within the application.
                    Adhering to these prescribed guidelines is important to maintain consistency
                    across all facets of the platform.
                </p>
            }
            pageContent={
                <>
                    <Section title="Absolute Time" contentArray={absoluteTime} />
                    <Section title="Relative Time" contentArray={relativeTime} />
                </>
            }
        />
    </div>
);
